/** Shared helpers for the print-window documents (prescription-print.ts, clinical-record-print.ts). */

/** Escape for safe interpolation into a print HTML string (values may be user-entered). */
export function esc(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

/** `value` if present, otherwise an em dash — for print rows where "empty" should still render. */
export function dash(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const text = String(value).trim();
  return text.length > 0 ? text : '—';
}

/** Lightens a `#rrggbb` hex color toward white by `amount` (0-1) — used for derived box tints. */
export function lighten(hex: string, amount: number): string {
  const match = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!match) return hex;
  const num = parseInt(match[1] ?? '000000', 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  const mix = (channel: number): number => Math.round(channel + (255 - channel) * amount);
  const toHex = (channel: number): string => channel.toString(16).padStart(2, '0');
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

// Cached by URL (not a single module-level variable) so different logos never collide.
const logo_data_uri_cache = new Map<string, Promise<string | null>>();

/**
 * Resolves a branding logo to a data URI so the print window never depends on a same-origin
 * image finishing its network load mid-print. `logo_url` may already be a data URI (an admin
 * uploaded a logo directly into Settings) — in that case it's returned unchanged, no fetch.
 */
export function resolveLogoDataUri(logo_url: string | null): Promise<string | null> {
  if (!logo_url) return Promise.resolve(null);
  if (logo_url.startsWith('data:')) return Promise.resolve(logo_url);
  const cached = logo_data_uri_cache.get(logo_url);
  if (cached) return cached;
  const promise = fetchLogoAsDataUri(logo_url);
  logo_data_uri_cache.set(logo_url, promise);
  return promise;
}

async function fetchLogoAsDataUri(logo_url: string): Promise<string | null> {
  try {
    const response = await fetch(logo_url, { cache: 'force-cache' });
    if (!response.ok) return null;
    const blob = await response.blob();
    return await blobToDataUri(blob);
  } catch {
    return null;
  }
}

function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('logo_read_failed'));
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('logo_read_failed'));
    };
    reader.readAsDataURL(blob);
  });
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export async function waitForPopupReady(popup: Window): Promise<void> {
  if (popup.document.readyState === 'complete') return;
  await Promise.race([
    new Promise<void>((resolve) => popup.addEventListener('load', () => resolve(), { once: true })),
    delay(1200),
  ]);
}

export async function waitForPopupImages(popup: Window): Promise<void> {
  const images = Array.from(popup.document.images ?? []);
  if (images.length === 0) return;
  await Promise.race([
    Promise.all(
      images.map((image) =>
        image.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              image.addEventListener('load', () => resolve(), { once: true });
              image.addEventListener('error', () => resolve(), { once: true });
            }),
      ),
    ).then(() => undefined),
    delay(1400),
  ]);
}
